import { logError } from "@entrilia/shared/logging.ts";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { FieldConfigurationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  AggregationDescriptor,
  ConditionField,
  CustomColumnField,
  DimensionDescriptor,
  DimensionField,
  GroupingField,
  MeasureDescriptor,
  MeasureField,
  MetricDescriptor,
  MetricField,
  ReportField,
  SortField,
  TabularConfiguration,
  TabularGeneralSettings,
} from "../../../../../../../../api/types/svTypes.ts";
import {
  AreaItemType,
  DimensionFieldType,
  ItemDataType,
  MeasureUsage,
  MetaItemType,
} from "../../../../../../../../api/types/svTypes.ts";
import { isDimensionBasedType } from "../../../../utilities/fieldsHelper.ts";

export const createTabularReportConfiguration = (
  conditions: ConditionField[],
  fields: ReportField[],
  sorts: SortField[],
  grouping: GroupingField[],
  settings: TabularGeneralSettings
) => {
  const reportConfiguration: TabularConfiguration = {
    conditions: conditions.map((c) => c.config),
    fields: fields.map((field) => field.config),
    sort: sorts.map((s) => ({
      name: s.config.name,
      id: s.config.id,
      ordering: s.config.ordering,
      caption: s.config.caption,
      isGroupField: s.config.isGroupField,
      type: s.config.type,
    })),
    grouping: [...grouping],
    settings: { ...settings },
  };
  return reportConfiguration;
};

export const configurationToTabularFields = (
  configs: FieldConfiguration[],
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  aggregations: AggregationDescriptor[] = []
) => {
  try {
    const tableFields = configs
      .map((config) => {
        if (isDimensionBasedType(config.type)) {
          if (config.persistedAggregationName) {
            const aggMeta = aggregations.find((a) => a.name === config.persistedAggregationName);
            if (aggMeta) {
              return <DimensionField>{
                type: DimensionFieldType.DIMENSION,
                meta: {
                  ...aggMeta,
                  itemType: MetaItemType.DIMENSION,
                  keyFieldName: "",
                  isNew: false,
                  availableFields: [],
                  displayValues: [],
                  dictionary: [],
                  glAllowed: false,
                },
                config,
                areaItemType: AreaItemType.FIELDS,
              };
            }
          }
          const dimensionMeta = dimensions.find((d) => d.name === config.name);
          if (dimensionMeta) {
            return <DimensionField>{
              type: DimensionFieldType.DIMENSION,
              meta: dimensionMeta,
              config,
              areaItemType: AreaItemType.FIELDS,
            };
          } else {
            //eslint-disable-next-line no-console
            console.log(`Field ${config.name} is not found in the dimensions' list`);
            return undefined;
          }
        } else if (config.type === FieldConfigurationType.Measure || config.type === FieldConfigurationType.Irr) {
          const measure = measures.find((m) => m.name === config.name);
          if (measure) {
            return <MeasureField>{
              type: DimensionFieldType.MEASURE,
              meta: measure,
              areaItemType: AreaItemType.FIELDS,
              config,
            };
          } else {
            //eslint-disable-next-line no-console
            console.log(`Measure ${config.name} is not found in the measures' list`);
            return undefined;
          }
        }
        if (config.type === FieldConfigurationType.Metric) {
          const metric = metrics.find((m) => m.name === config.name);
          if (metric) {
            return <MetricField>{
              type: DimensionFieldType.METRIC,
              meta: metric,
              areaItemType: AreaItemType.FIELDS,
              config,
            };
          } else {
            //eslint-disable-next-line no-console
            console.log(`Metric ${config.name} is not found in the metrics' list`);
            return undefined;
          }
        }
        if (config.type === FieldConfigurationType.Custom) {
          return <CustomColumnField>{
            type: DimensionFieldType.CUSTOM,
            meta: {
              caption: config.name,
              type: ItemDataType.Numeric,
              name: config.name,
              itemType: MetaItemType.CUSTOM,
              usage: MeasureUsage.General,
              group: "",
            },
            areaItemType: AreaItemType.FIELDS,
            config,
          };
        }
        return undefined;
      })
      .filter((field): field is ReportField => !!field);
    return tableFields;
  } catch {
    return [];
  }
};

export const configurationToGrouping = (grouping: GroupingField[]) => {
  try {
    return structuredClone(grouping);
  } catch (e) {
    logError(e, "Error cloning tabular grouping");
    return [];
  }
};
